
import { Component, forwardRef, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { debounceTime, filter, Subscription, switchMap, tap } from "rxjs";
import { Question } from "src/app/classes/dynamic-form";
import { DynamicFormEditorService } from 'src/app/services/dynamic-form-editor.service';
import { QuestionService } from 'src/app/services/question.service';
import { QuestionBuilderBranchComponent } from './question-builder-branch.component';
import { flyInOut, foldInOut } from 'src/app/animations';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';


@Component( {
	standalone: true,
	selector: "question-builder",
	templateUrl: "./question-builder.component.html",
	styleUrl: "./question-builder.component.scss",
	animations: [
		flyInOut,
		foldInOut
	],
	imports: [
		FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule,
		MatSelectModule, MatCheckboxModule,
		MatButtonModule, MatProgressSpinnerModule,
		forwardRef( () => QuestionBuilderBranchComponent ),		// forwardRef to avoid a circular dependency issue
	]
})
export class QuestionBuilderComponent implements OnInit, OnDestroy {


	@Input() question = new Question();
	@Input() siblings: Question[] = [];

	questionBuilderFormGroup = new FormGroup( {
		label: new FormControl<string>( "", Validators.required ),
		type: new FormControl<"check"|"number"|"text"|"textarea"|"MehrwachUnterfragen">( "check", Validators.required ),
		required: new FormControl<boolean>( false ),
		deregistration: new FormControl<boolean>( false ),
		childrenCondition: new FormControl<string>( "" ),
		exclusion: new FormControl<string>( "" ),
	});

	subscriptions = new Subscription();

	bSaving = false;
	tmsLastSaveStart: number|null = null;
	tmsLastSaveEnd: number|null = null;
	bQuestionUpdateError = false;

	bCreationInProgress = false;
	bKidCreationError = false;
	bDeleteError = false;

	bShowKids = true;

	bLockedForm = false;


	constructor(
		private dynamicFormEditorService: DynamicFormEditorService,
		private questionService: QuestionService
	) {}


	ngOnInit(): void {

		this.subscriptions.add(
			this.questionBuilderFormGroup.valueChanges.pipe( 
				debounceTime( 500 ),
				filter( v => this.questionBuilderFormGroup.valid ),
				switchMap( v => {

					this.question.label = v.label || "";
					if( v.type == "MehrwachUnterfragen" ) {
						this.question.type = "number";
						this.question.bIsLoop = true;
					}
					else {
						this.question.type = v.type || "text";
						this.question.bIsLoop = false;
					}
					this.question.required = v.required || false;
					this.question.deregistration = v.deregistration || false;
					this.question.childrenCondition = v.childrenCondition || "";
					this.question.excludesQuestionId = v.exclusion || "";

					this.tmsLastSaveStart = (new Date()).getTime();
					this.bSaving = true;
					return this.questionService.updateQuestion( this.question ).pipe( 
						
						tap( b => {

							this.bSaving = false;
							this.bQuestionUpdateError = !b;
							if( !b ) {
								this.tmsLastSaveStart = null;
								this.tmsLastSaveEnd = null;
							}
							else {
								this.tmsLastSaveEnd = (new Date()).getTime();
							}

						})

					);
				} )

			).subscribe()
		);

		this.questionBuilderFormGroup.patchValue( {
				label: this.question.label,
				type: this.question.bIsLoop ? "MehrwachUnterfragen" : (this.question.type || "text"),
				required: this.question.required,
				deregistration: this.question.deregistration,
				childrenCondition: this.question.childrenCondition,
				exclusion: this.question.excludesQuestionId
			},
			{ emitEvent: false }
		);

		if( this.question.bIsDisabled ) {
			this.questionBuilderFormGroup.disable();
			this.bLockedForm = true;
		}

	}

	ngOnDestroy(): void {

		this.subscriptions.unsubscribe();

	}


	clickAddQuestion() {

		if( this.bSaving || this.bCreationInProgress ) {
			console.log( "another operation is already in progress" );
			return;
		}

		this.bCreationInProgress = true;

		this.dynamicFormEditorService.generateQuestion( this.question.id ).subscribe( id => {

			this.bCreationInProgress = false;
			this.bKidCreationError = !id;

		});

	}

	clickRemoveQuestion() {

		if( this.bSaving ) {
			console.log( "another operation is already in progress" );
			return;
		}

		this.bSaving = true;

		this.dynamicFormEditorService.deleteQuestionAndKids( this.question.id ).subscribe( b => {
			this.bDeleteError = !b;
			this.bSaving = false;
		});

	}

}
